import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash, ArrowCounterClockwise, CheckCircle, XCircle, Gear, ClockCounterClockwise, DownloadSimple } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../auth/AuthContext';
import CustomerOverridesModal from '../components/CustomerOverridesModal';
import DiscountHistoryModal from '../components/DiscountHistoryModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { formatNzDate, formatNzDateTime } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABELS = { admin: 'Admin', sales_rep: 'Sales Rep', customer: 'Customer' };

const UsersPage = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'customer' });
  const [tempPassword, setTempPassword] = useState(null); // { email, password } after create / reset
  const [overridesUser, setOverridesUser] = useState(null); // customer whose overrides modal is open
  const [historyUser, setHistoryUser] = useState(null);     // customer whose discount-history modal is open
  const [resetUser, setResetUser] = useState(null);   // user awaiting password-reset confirm
  const [deleteUser, setDeleteUser] = useState(null); // user awaiting delete confirm
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const isAdmin = me?.role === 'admin';
  const allowedRolesToCreate = isAdmin ? ['admin', 'sales_rep', 'customer'] : ['customer'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`);
      setUsers(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitNew = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/users`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
      });
      setTempPassword({ email: data.email, password: data.temporaryPassword });
      setForm({ name: '', email: '', role: 'customer' });
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API}/users/${u.id}`, { active: !u.active });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resetPassword = (u) => setResetUser(u);

  const runResetPassword = async () => {
    if (!resetUser) return;
    const u = resetUser;
    setConfirmBusy(true);
    try {
      const { data } = await axios.post(`${API}/users/${u.id}/reset-password`);
      setTempPassword({ email: u.email, password: data.temporaryPassword });
      setResetUser(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConfirmBusy(false);
    }
  };

  const remove = (u) => setDeleteUser(u);

  const runRemove = async () => {
    if (!deleteUser) return;
    const u = deleteUser;
    setConfirmBusy(true);
    try {
      await axios.delete(`${API}/users/${u.id}`);
      setDeleteUser(null);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-1" data-testid="users-page-title">User Management</h1>
            <p className="text-text-secondary">Create and manage staff & customer accounts.</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${API}/discount-structures/export`}
              data-testid="export-all-discounts-button"
              title="Download a backup CSV of every customer's current discount structure"
              className="flex items-center gap-2 px-4 py-2 border border-border-color text-text-primary rounded-lg hover:border-gold hover:text-gold"
            >
              <DownloadSimple size={18} weight="bold" /> Export discounts CSV
            </a>
            <button
              onClick={() => { setShowForm((s) => !s); setError(null); }}
              data-testid="add-user-button"
              className="flex items-center gap-2 px-4 py-2 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
            >
              <Plus size={18} weight="bold" /> Add User
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-danger/10 border border-danger/30 text-danger rounded-lg text-sm" data-testid="users-error">
            {error}
          </div>
        )}

        {tempPassword && (
          <div className="mb-4 px-4 py-3 bg-gold/10 border border-gold/30 text-gold rounded-lg text-sm" data-testid="users-temp-password">
            <p className="font-semibold mb-1">Temporary password for {tempPassword.email}</p>
            <p className="font-mono text-base text-text-primary">{tempPassword.password}</p>
            <p className="mt-1 text-xs text-text-secondary">Hand this to the user — they should change it after signing in. This message clears when you reload the page.</p>
          </div>
        )}

        {showForm && (
          <form onSubmit={submitNew} className="bg-surface border border-border-color rounded-xl p-6 mb-6 space-y-4" data-testid="user-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Name</label>
                <input
                  type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="user-name-input"
                  className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Email</label>
                <input
                  type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="user-email-input"
                  className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-text-secondary mb-2 block">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="user-role-select"
                  className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                >
                  {allowedRolesToCreate.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-text-secondary">A temporary password will be generated and shown to you so you can hand it to the user.</p>
            <div className="flex gap-3">
              <button type="submit" disabled={busy} data-testid="user-form-submit" className="px-4 py-2 bg-gold text-black font-bold rounded-lg disabled:opacity-50">
                {busy ? 'Creating…' : 'Create User'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-border-color text-text-primary rounded-lg">Cancel</button>
            </div>
          </form>
        )}

        <div className="bg-surface border border-border-color rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-black/40">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Role</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-text-secondary">Last login</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-text-secondary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-6 py-6 text-center text-text-secondary">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-6 text-center text-text-secondary">No users yet.</td></tr>
              )}
              {users.map((u) => {
                const canManage = isAdmin || u.role === 'customer';
                return (
                  <tr key={u.id} className="border-t border-border-color" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4 text-text-primary">{u.name}</td>
                    <td className="px-6 py-4 text-text-secondary">
                      <div>{u.email}</div>
                      {u.role === 'customer' && (
                        u.confidentialityAcceptedAt ? (
                          <div
                            className="text-[10px] text-success/80 mt-0.5"
                            data-testid={`user-confidentiality-ack-${u.id}`}
                            title={`Acknowledged at ${formatNzDateTime(u.confidentialityAcceptedAt)} (NZ time)`}
                          >
                            Conf. acknowledged · {formatNzDate(u.confidentialityAcceptedAt)}
                          </div>
                        ) : (
                          <div
                            className="text-[10px] text-warning/80 mt-0.5"
                            data-testid={`user-confidentiality-pending-${u.id}`}
                          >
                            Conf. acknowledgement pending
                          </div>
                        )
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-1">
                        <span className="px-2 py-1 bg-gold/10 text-gold text-xs rounded">{ROLE_LABELS[u.role] || u.role}</span>
                        {u.role === 'customer' && (
                          u.singleTubEnabled === true ? (
                            <span
                              className="px-2 py-0.5 whitespace-nowrap bg-success/10 text-success border border-success/30 text-[10px] rounded"
                              data-testid={`user-single-tub-on-${u.id}`}
                              title="Single Tub tier is enabled for this customer"
                            >
                              Single Tub On
                            </span>
                          ) : (
                            <span
                              className="px-2 py-0.5 whitespace-nowrap text-text-secondary border border-border-color text-[10px] rounded"
                              data-testid={`user-single-tub-off-${u.id}`}
                              title="Single Tub tier is off (default) — enable it via Overrides"
                            >
                              Single Tub Off
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {u.active
                        ? <span className="inline-flex items-center gap-1 text-success text-xs"><CheckCircle size={14} weight="fill" /> Active</span>
                        : <span className="inline-flex items-center gap-1 text-text-secondary text-xs"><XCircle size={14} weight="fill" /> Disabled</span>}
                    </td>
                    <td className="px-6 py-4 text-text-secondary text-xs">{u.lastLoginAt ? formatNzDateTime(u.lastLoginAt) : '—'}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canManage && (
                          <>
                            {u.role === 'customer' && (
                              <>
                                <button
                                  onClick={() => setOverridesUser(u)}
                                  data-testid={`user-overrides-${u.id}`}
                                  className="text-xs px-2 py-1 border border-border-color text-text-secondary rounded hover:border-gold hover:text-gold inline-flex items-center gap-1"
                                  title="Per-customer discounts & product visibility"
                                >
                                  <Gear size={12} weight="bold" /> Overrides
                                </button>
                                <button
                                  onClick={() => setHistoryUser(u)}
                                  data-testid={`user-discount-history-${u.id}`}
                                  className="text-xs px-2 py-1 border border-border-color text-text-secondary rounded hover:border-gold hover:text-gold inline-flex items-center gap-1"
                                  title="Discount structure history & CSV export"
                                >
                                  <ClockCounterClockwise size={12} weight="bold" /> History
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => toggleActive(u)}
                              data-testid={`user-toggle-${u.id}`}
                              className="text-xs px-2 py-1 border border-border-color text-text-secondary rounded hover:border-gold hover:text-gold"
                            >
                              {u.active ? 'Disable' : 'Enable'}
                            </button>
                            <button
                              onClick={() => resetPassword(u)}
                              data-testid={`user-reset-${u.id}`}
                              className="text-xs px-2 py-1 border border-border-color text-text-secondary rounded hover:border-gold hover:text-gold inline-flex items-center gap-1"
                              title="Reset password"
                            >
                              <ArrowCounterClockwise size={12} weight="bold" /> Reset
                            </button>
                            {u.id !== me?.id && (
                              <button
                                onClick={() => remove(u)}
                                data-testid={`user-delete-${u.id}`}
                                className="text-xs p-1 text-text-secondary rounded hover:text-danger"
                                aria-label="Delete user"
                              >
                                <Trash size={14} weight="bold" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {overridesUser && (
        <CustomerOverridesModal
          user={overridesUser}
          onClose={(saved) => {
            setOverridesUser(null);
            if (saved) load();
          }}
        />
      )}

      {historyUser && (
        <DiscountHistoryModal user={historyUser} onClose={() => setHistoryUser(null)} />
      )}

      <ConfirmDialog
        open={!!resetUser}
        title="Reset password?"
        message={resetUser ? `Generate a new temporary password for ${resetUser.email}? The current password will stop working immediately.` : ''}
        confirmLabel="Reset password"
        destructive={false}
        busy={confirmBusy}
        onCancel={() => setResetUser(null)}
        onConfirm={runResetPassword}
        testIdPrefix="confirm-reset-password"
      />
      <ConfirmDialog
        open={!!deleteUser}
        title="Delete user?"
        message={deleteUser ? `Delete user ${deleteUser.email}? This cannot be undone.` : ''}
        confirmLabel="Delete"
        busy={confirmBusy}
        onCancel={() => setDeleteUser(null)}
        onConfirm={runRemove}
        testIdPrefix="confirm-delete-user"
      />
    </div>
  );
};

export default UsersPage;
